// src/pages/Main.js — 오늘 대시보드 (로그인) / 첫 화면 (비로그인)
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCodeSearch, getDietHistCalendar, getDietHistDayInfo, getUserInfo } from '../api/dietApi';
import { isTokenValid } from '../components/RequireLogin';
import { MACROS, MacroStrip, NutrientBar, fmt } from '../components/ui/Nutrients';
import './Main.css';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
const DEFAULT_MEALS = [
  { code: 'BR', codeNm: '아침' }, { code: 'LC', codeNm: '점심' },
  { code: 'DN', codeNm: '저녁' }, { code: 'SN', codeNm: '간식' },
];
const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sum = (list, key) => list.reduce((s, x) => s + (Number(x[key]) || 0), 0);

// 목표 g = 희망칼로리 × 비율 ÷ (탄·단 4kcal/g, 지 9kcal/g)
const goalGrams = (u) => ({
  carbo: (u.wantKcal * u.carboRate) / 100 / 4,
  protein: (u.wantKcal * u.protRate) / 100 / 4,
  fat: (u.wantKcal * u.fatRate) / 100 / 9,
});

const hasGoal = (u) => u && [u.wantKcal, u.carboRate, u.protRate, u.fatRate].every((v) => v !== null && v !== undefined && v !== '');

export default function Main() {
  const token = localStorage.getItem('token');
  const authed = !!token && isTokenValid(token);
  return authed ? <Dashboard /> : <Welcome />;
}

function Welcome() {
  const navigate = useNavigate();
  const onSearch = (e) => {
    e.preventDefault();
    const q = e.target.foodNm.value.trim();
    navigate(q ? `/food-search?foodNm=${encodeURIComponent(q)}` : '/food-search');
  };
  return (
    <div className="welcome">
      <h2>오늘 먹은 걸 기록하고, 다음 끼니를 추천받으세요</h2>
      <p>음식 16만 개의 영양 정보를 바로 찾아볼 수 있어요.</p>
      <form className="welcome-search" onSubmit={onSearch}>
        <input className="input" name="foodNm" placeholder="음식 이름으로 검색 (예: 현미밥)" autoComplete="off" />
        <button className="btn primary" type="submit">검색</button>
      </form>
      <div className="welcome-cta">
        <button className="btn primary" type="button" onClick={() => navigate('/login')}>로그인하고 오늘 식단 기록하기</button>
        <button className="btn" type="button" onClick={() => navigate('/signup')}>회원가입</button>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const todayStr = ymd(today);

  const [user, setUser] = useState(null);
  const [mealCodes, setMealCodes] = useState(DEFAULT_MEALS);
  const [dayItems, setDayItems] = useState([]);
  const [weekKcal, setWeekKcal] = useState({});
  const [loading, setLoading] = useState(true);
  const [animate, setAnimate] = useState(false);

  // 일~토 한 주
  const weekDays = useMemo(() => {
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [today]);

  useEffect(() => {
    // 주가 두 달에 걸치면 달마다 캘린더 조회
    const months = [...new Set(weekDays.map((d) => `${d.getFullYear()}-${d.getMonth() + 1}`))];
    Promise.all([
      getUserInfo().then((r) => r.data).catch(() => null),
      getCodeSearch('dietCd').then((r) => r.data).catch(() => null),
      getDietHistDayInfo(todayStr).then((r) => r.data).catch(() => []),
      ...months.map((m) => {
        const [y, mo] = m.split('-');
        return getDietHistCalendar(y, mo).then((r) => ({ y, mo, list: r.data })).catch(() => ({ y, mo, list: [] }));
      }),
    ]).then(([u, codes, day, ...cal]) => {
      setUser(u);
      if (Array.isArray(codes) && codes.length) setMealCodes(codes);
      setDayItems(Array.isArray(day) ? day : []);
      const map = {};
      cal.forEach(({ y, mo, list }) => (Array.isArray(list) ? list : []).forEach((it) => {
        map[`${y}-${pad(mo)}-${pad(it.histDay)}`] = Number(it.dayKcal) || 0;
      }));
      setWeekKcal(map);
      setLoading(false);
      requestAnimationFrame(() => setAnimate(true));
    });
  }, [todayStr, weekDays]);

  // 오늘 먹은 음식 → 끼니별 (DietRegister 와 같은 dietHistSeq+dietCd 그룹)
  const meals = useMemo(() => {
    const map = new Map();
    dayItems.forEach((it) => {
      const key = `${it.dietHistSeq}_${it.dietCd}`;
      if (!map.has(key)) map.set(key, { type: it.dietCd, foods: [] });
      map.get(key).foods.push({
        name: it.foodNm, intake: it.intake,
        kcal: it.kcalCalc ?? 0, carbo: it.carboCalc ?? 0, protein: it.proteinCalc ?? 0, fat: it.fatCalc ?? 0,
      });
    });
    return [...map.values()];
  }, [dayItems]);

  const allFoods = meals.flatMap((m) => m.foods);
  const eaten = { kcal: sum(allFoods, 'kcal'), carbo: sum(allFoods, 'carbo'), protein: sum(allFoods, 'protein'), fat: sum(allFoods, 'fat') };

  if (loading) return <div className="dash-loading">불러오는 중…</div>;

  const goalOk = hasGoal(user);
  const goal = goalOk ? Number(user.wantKcal) : 0;
  const grams = goalOk ? goalGrams({ wantKcal: goal, carboRate: Number(user.carboRate), protRate: Number(user.protRate), fatRate: Number(user.fatRate) }) : null;
  const over = goalOk && eaten.kcal > goal;
  const ratio = goalOk ? Math.min(eaten.kcal / goal, 1) : 0;
  const dayHref = `/diet/${todayStr}`;

  return (
    <div className="dash">
      <p className="dash-hello">{user?.userName ? `${user.userName}님, ` : ''}오늘도 잘 챙겨 드세요</p>

      {/* 오늘 섭취 */}
      <section>
        <div className="dash-section-head">
          <h2>오늘 섭취</h2>
          <Link className="dash-link" to="/mypage">목표 수정</Link>
        </div>
        {goalOk ? (
          <div className="dash-summary">
            <div className={`dash-ring${over ? ' over' : ''}`}>
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle className="dash-ring-track" cx="60" cy="60" r={RING_R} fill="none" strokeWidth="10" />
                <circle
                  className="dash-ring-bar" cx="60" cy="60" r={RING_R} fill="none" strokeWidth="10" strokeLinecap="round"
                  style={{ strokeDasharray: RING_C, strokeDashoffset: animate ? RING_C * (1 - ratio) : RING_C }}
                />
              </svg>
              <div className="dash-ring-label">
                <span className="dash-ring-value">{fmt(eaten.kcal)}</span>
                <span className="dash-ring-goal">/ {fmt(goal)} kcal</span>
                <span className="dash-ring-left">{over ? `${fmt(eaten.kcal - goal)} kcal 초과` : `${fmt(goal - eaten.kcal)} kcal 남음`}</span>
              </div>
            </div>
            <div className="dash-macros">
              {MACROS.map((m) => (
                <NutrientBar key={m.key} label={m.label} color={m.color} value={animate ? eaten[m.key] : 0} goal={grams[m.key]} />
              ))}
              <p className="dash-goal-note">마이페이지 영양 목표 기준 · 탄 {user.carboRate}% · 단 {user.protRate}% · 지 {user.fatRate}%</p>
            </div>
          </div>
        ) : (
          <div className="dash-goal-empty">
            <div>
              <strong>영양 목표를 먼저 설정해 주세요</strong>
              <p>희망 칼로리와 탄수화물·단백질·지방 비율을 입력하면 오늘 섭취량과 맞춤 식단 추천을 볼 수 있어요.</p>
            </div>
            <button className="btn primary" type="button" onClick={() => navigate('/mypage')}>마이페이지에서 목표 입력</button>
          </div>
        )}
      </section>

      {/* 끼니별 기록 */}
      <section>
        <div className="dash-section-head">
          <h2>끼니별 기록</h2>
          <Link className="dash-link" to={dayHref}>오늘 식단 편집</Link>
        </div>
        <div className="dash-meals">
          {mealCodes.map(({ code, codeNm }) => {
            const ms = meals.filter((m) => m.type === code);
            const foods = ms.flatMap((m) => m.foods);
            if (!foods.length) {
              return (
                <Link key={code} className="dash-meal empty" to={dayHref}>
                  <span className="dash-plus">+</span>
                  <span className="dash-meal-type">{codeNm} 기록하기</span>
                </Link>
              );
            }
            return (
              <Link key={code} className="dash-meal" to={dayHref}>
                <div className="dash-meal-head">
                  <span className="dash-meal-type">{codeNm}</span>
                  <span className="dash-meal-kcal">{fmt(sum(foods, 'kcal'))} kcal</span>
                </div>
                <ul className="dash-meal-foods">
                  {foods.map((f, i) => <li key={i}><span>{f.name}</span><span>{fmt(f.intake)}g</span></li>)}
                </ul>
                <div className="dash-meal-mini">
                  <MacroStrip carbo={sum(foods, 'carbo')} protein={sum(foods, 'protein')} fat={sum(foods, 'fat')} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 이번 주 */}
      <section>
        <div className="dash-section-head">
          <h2>이번 주</h2>
          <Link className="dash-link" to="/DietCalendar">캘린더 전체 보기</Link>
        </div>
        <div className="dash-week">
          {weekDays.map((d, i) => {
            const key = ymd(d);
            const isToday = key === todayStr;
            const kcal = isToday ? eaten.kcal : (weekKcal[key] || 0);
            const cls = ['dash-day', isToday && 'today', i === 0 && 'sun', i === 6 && 'sat'].filter(Boolean).join(' ');
            return (
              <Link key={key} className={cls} to={`/diet/${key}`}>
                <span className="dash-day-w">{WEEK[i]}</span>
                <span className="dash-day-d">{d.getDate()}</span>
                <span className="dash-day-kcal">{kcal ? fmt(kcal) : ''}</span>
                <span className={`dash-day-dot${kcal ? '' : ' none'}`} />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
